import React, { createContext, useContext, useMemo } from 'react';
import numbro from 'numbro';
import { useUpdateUserSettingsActions } from '@data/hooks/currentUser';
import { formatDateWithLocale, parseDateWithLocale } from '@util/date';
import { createNumberFormater, dateToSerial, parseNumberWithLocale } from '@util/number';
import { localeData } from './localeData';
import { useAuth } from '../providers/AuthProvider';
function initLocale(l) {
    var locale = l !== null && l !== void 0 ? l : 'en-US';
    return function (key) {
        var _a;
        if (!(locale in localeData)) {
            return key;
        }
        if ((_a = key in localeData[locale]) !== null && _a !== void 0 ? _a : {}) {
            return localeData[locale][key];
        }
        return key;
    };
}
var LanguageContext = createContext({
    L: initLocale(),
    changeLocalesAndPalette: function () { },
    excelLocalizedNumberFormat: function (value, format) {
        if (format === void 0) { format = ''; }
        if (value === null) {
            return '';
        }
        var formater = createNumberFormater(format, 'de-DE');
        return formater(value);
    },
    parseLocaleNumber: function (value) {
        if (value === null) {
            return null;
        }
        else {
            return parseNumberWithLocale(value, 'de-DE');
        }
    },
    excelLocalizedDateFormat: function (value, format) {
        if (format === void 0) { format = ''; }
        if (value === null) {
            return '';
        }
        var formater = createNumberFormater(format, 'en-US');
        var numberValue = typeof value === 'number' ? value : dateToSerial(value);
        if (numberValue === null) {
            return '';
        }
        return formater(numberValue);
    },
    formatLocaleDate: function (value) {
        return formatDateWithLocale(value, 'en');
    },
    parseLocaleDate: function (value) {
        if (value === undefined) {
            return null;
        }
        return parseDateWithLocale(value, 'en');
    },
});
function LanguageProvider(_a) {
    var children = _a.children;
    var _b = useAuth().settings, user_app_language = _b.user_app_language, user_currency_locale_locale = _b.user_currency_locale_locale, user_date_locale = _b.user_date_locale;
    var setLocalesAndPalette = useUpdateUserSettingsActions().setLocalesAndPalette;
    var context = useMemo(function () { return ({
        changeLocalesAndPalette: function (updates) {
            setLocalesAndPalette(updates);
            if (updates.currencyLocale !== undefined) {
                numbro.setLanguage(updates.currencyLocale);
            }
        },
        L: initLocale(user_app_language),
        excelLocalizedNumberFormat: function (value, format) {
            if (format === void 0) { format = ''; }
            if (value === null) {
                return '';
            }
            var formater = createNumberFormater(format, user_currency_locale_locale);
            return formater(value);
        },
        parseLocaleNumber: function (value) {
            if (value === null) {
                return null;
            }
            else {
                return parseNumberWithLocale(value, user_currency_locale_locale);
            }
        },
        excelLocalizedDateFormat: function (value, format) {
            if (format === void 0) { format = ''; }
            if (value === null) {
                return '';
            }
            var formater = createNumberFormater(format, user_date_locale);
            var numberValue = typeof value === 'number' ? value : dateToSerial(value);
            if (numberValue === null) {
                return '';
            }
            return formater(numberValue);
        },
        formatLocaleDate: function (value) {
            return formatDateWithLocale(value, user_date_locale);
        },
        parseLocaleDate: function (value) {
            if (value === undefined) {
                return null;
            }
            return parseDateWithLocale(value, user_date_locale);
        },
    }); }, [user_app_language, user_currency_locale_locale, user_date_locale]);
    return React.createElement(LanguageContext.Provider, { value: context }, children);
}
export default LanguageProvider;
export function useLanguage() {
    return useContext(LanguageContext);
}
